import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// utils
import { countries } from 'src/utils/country';
import { API_BASE_URL } from 'src/config-global';

// ----------------------------------------------------------------------

// Validation schema for form fields
const validationSchema = {
  firstName: {
    required: true,
    label: 'First name',
  },
  lastName: {
    required: true,
    label: 'Last name',
  },
  email: {
    required: true,
    label: 'Email',
    pattern: /\S+@\S+\.\S+/,
    patternMessage: 'Please enter a valid email',
  },
  company: {
    required: true,
    label: 'Company name',
  },
  phone: {
    required: true,
    label: 'Phone number',
  },
  role: {
    required: true,
    label: 'Job role',
  },
  roleName: {
    required: (formData) => formData.role === 'Other',
    label: 'Role specification',
    dependsOn: 'role',
  },
};

// Generic validation function
const validateField = (fieldName, value, formData = {}) => {
  const field = validationSchema[fieldName];
  if (!field) return '';

  // Check if field is conditionally required
  if (field.required && typeof field.required === 'function') {
    if (!field.required(formData)) return '';
  } else if (field.required && !value.trim()) {
    return `${field.label} is required`;
  }

  // Check pattern validation
  if (field.pattern && !field.pattern.test(value)) {
    return field.patternMessage || `Invalid ${field.label.toLowerCase()}`;
  }

  return '';
};

// Validate entire form
const validateForm = (formData) => {
  const errors = {};

  Object.keys(validationSchema).forEach((fieldName) => {
    const error = validateField(fieldName, formData[fieldName], formData);
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};

export default function RequestDemo({
  buttonText = 'Request Demo',
  buttonVariant = 'contained',
  buttonColor = 'primary',
  buttonSize = 'medium',
  fullWidth = false,
  sx,
  onDemoRequest,
  title = 'Request a Demo',
  description = "Fill out the form below and we'll get back to you within 24 hours to schedule your personalized demo.",
  startIcon,
  defaultRole = 'Fnb Operator',
  showRoleSelection = true,
  customRoles = ['Fnb Operator', 'Supplier', 'Distributor', 'Other'],
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    countryCode: '+1',
    role: defaultRole,
    roleName: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const open = useBoolean();

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Real-time validation
    const error = validateField(field, value, { ...formData, [field]: value });
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleCountryChange = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        countryCode: `+${newValue.phone}`,
      });
    }
  };

  // Map form role values to API expected format
  const mapRoleToAPI = (role) => {
    const roleMap = {
      'Fnb Operator': 'fnb_operator',
      Supplier: 'supplier',
      Distributor: 'distributor',
      Other: 'other',
    };
    return roleMap[role] || 'fnb_operator';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare request body with snake_case format
      const requestBody = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        company_name: formData.company,
        email: formData.email,
        country_code: formData.countryCode,
        phone_number: formData.phone,
        role: mapRoleToAPI(formData.role),
        message: formData.message || '',
      };

      const response = await fetch(`${API_BASE_URL}/v1/web/request-demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit form. Please try again.');
      }

      // Call optional callback if provided
      if (onDemoRequest) {
        await onDemoRequest(formData);
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        open.onFalse();
        setSubmitSuccess(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          phone: '',
          countryCode: '+1',
          role: defaultRole,
          roleName: '',
          message: '',
        });
        setErrors({});
      }, 2000);
    } catch (error) {
      console.error('Demo request failed:', error);
      setErrors({
        submit: error.message || 'An error occurred. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    open.onFalse();
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      phone: '',
      countryCode: '+1',
      role: defaultRole,
      roleName: '',
      message: '',
    });
    setErrors({});
    setSubmitSuccess(false);
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    const newErrors = validateForm(formData);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        color={buttonColor}
        size={buttonSize}
        fullWidth={fullWidth}
        onClick={open.onTrue}
        startIcon={startIcon}
        sx={sx}
      >
        {buttonText}
      </Button>

      <Dialog
        open={open.value}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, px: 3, pt: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 4 }}>
                Thank you! We&apos;ve received your demo request and will contact you within 24
                hours.
              </Alert>
            )}

            {errors.submit && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {errors.submit}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* First Name and Last Name Row */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    name="firstName"
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    name="lastName"
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Company Name */}
              <TextField
                required
                fullWidth
                name="company"
                label="Company Name"
                value={formData.company}
                onChange={handleInputChange('company')}
                error={!!errors.company}
                helperText={errors.company}
                size="small"
              />

              {/* Email */}
              <TextField
                required
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
              />

              {/* Country Code and Phone Number Row */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={countries}
                    autoHighlight
                    getOptionLabel={(option) => `${option.code} (+${option.phone})`}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                        {option.code} (+{option.phone})
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Country Code"
                        size="small"
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      />
                    )}
                    onChange={handleCountryChange}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Role Selection */}
              {showRoleSelection && (
                <>
                  <FormControl fullWidth size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={formData.role}
                      label="Role"
                      onChange={handleInputChange('role')}
                      error={!!errors.role}
                    >
                      {customRoles.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Conditional Role Specification */}
                  {formData.role === 'Other' && (
                    <TextField
                      fullWidth
                      name="roleName"
                      label="Please specify your role"
                      value={formData.roleName}
                      onChange={handleInputChange('roleName')}
                      error={!!errors.roleName}
                      helperText={errors.roleName}
                      size="small"
                    />
                  )}
                </>
              )}

              {/* Additional Message */}
              <TextField
                fullWidth
                name="message"
                label="Additional Message (Optional)"
                multiline
                rows={3}
                value={formData.message}
                onChange={handleInputChange('message')}
                size="small"
                placeholder="Tell us more about your needs or any specific questions you have..."
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isFormValid()}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'grey.400',
                  color: 'grey.600',
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

RequestDemo.propTypes = {
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  buttonColor: PropTypes.oneOf(['primary', 'secondary', 'error', 'info', 'success', 'warning']),
  buttonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
  onDemoRequest: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
  startIcon: PropTypes.node,
  defaultRole: PropTypes.string,
  showRoleSelection: PropTypes.bool,
  customRoles: PropTypes.arrayOf(PropTypes.string),
};
